import React, { useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Task } from '../../../../types/task';
import { TaskItemV2 } from './TaskItemV2';
import { TimelineView } from './TimelineView';
import { SmartEmptyState } from './SmartEmptyState';
import { RecentTimeGroup } from './RecentTimeGroup';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { taskV2FocusedTaskIndexAtom, taskV2TemporaryVisibleTasksAtom } from '../../../../atoms/v3/taskV2Atoms';
import { isTaskNaturallyOnDate, isTaskShownByDoneAt } from '../../../../utils/taskTimeUtils';
import { getWelcomeProjectId } from '../../../../utils/welcomeProjectUtils';
import { groupTasksByTime } from '../../../../utils/taskUtils';

interface TaskV2ListProps {
  tasks: Task[];
  viewMode: 'list' | 'timeline';
  specialMode: 'past' | 'future' | 'notime' | 'recent' | null;
  currentDate: Date;
  stackMode?: boolean; // 新增：堆叠模式
  allTasks: Task[]; // 完整任务列表，用于智能空状态分析
  onTaskClick: (taskId: string) => void;
  onTaskToggle: (taskId: string, completed: boolean) => void;
  onTaskUpdate?: (updateData: any) => Promise<boolean>;
  onDateChange: (date: Date) => void; // 用于空状态导航
  onNewTask: (initialData?: { startAt?: string; cost?: number }) => void; // 用于空状态创建任务，支持初始数据
}

/**
 * TaskV2 任务列表容器组件
 * 根据视图模式渲染不同的任务列表
 */
export const TaskV2List: React.FC<TaskV2ListProps> = ({
  tasks,
  viewMode,
  specialMode,
  currentDate,
  stackMode = false,
  allTasks,
  onTaskClick,
  onTaskToggle,
  onTaskUpdate,
  onDateChange,
  onNewTask
}) => {
  const { t } = useTranslation(['v3/tasks']);
  const [isDark] = useAtom(isDarkAtom);
  const focusedTaskIndex = useAtomValue(taskV2FocusedTaskIndexAtom);
  const temporaryVisibleTasks = useAtomValue(taskV2TemporaryVisibleTasksAtom);

  // 获取欢迎项目ID用于标识
  const welcomeProjectId = getWelcomeProjectId();

  // 分类任务并创建统一的视觉顺序映射
  const { naturalTasks, completedTodayTasks, temporaryTasks, visualOrderMap } = useMemo(() => {
    if (!tasks || tasks.length === 0) {
      return {
        naturalTasks: [],
        completedTodayTasks: [],
        temporaryTasks: [],
        visualOrderMap: new Map(),
        visualTaskList: []
      };
    }

    const natural: Task[] = [];
    const completedToday: Task[] = [];
    const temporary: Task[] = [];

    // 先分类
    if (specialMode) {
      // 特殊模式下，所有任务都作为自然任务显示
      tasks.forEach((task) => {
        natural.push(task);
      });
    } else {
      // 正常模式下，按日期分类
      tasks.forEach((task) => {
        const isNaturallyToday = isTaskNaturallyOnDate(task, currentDate);
        const isCompletedToday = isTaskShownByDoneAt(task, currentDate);
        const isTemporary = temporaryVisibleTasks.has(task.id);

        if (isNaturallyToday) {
          // 真正属于今天的任务（最高优先级）
          natural.push(task);
        } else if (isCompletedToday) {
          // 完成于今天的其他日期任务
          completedToday.push(task);
        } else if (isTemporary) {
          // 临时缓存的任务（undone 后的任务，doneAt 已被后端清空）
          // 这些任务应该显示在"完成于今天"分组下方
          temporary.push(task);
        }
      });
    }

    // 对三个列表分别排序（使用相同的排序逻辑）
    const sortedNatural = [...natural].sort((a, b) => {
      // 1. 完成状态：未完成在前
      if (a.done !== b.done) {
        return a.done ? 1 : -1;
      }

      // 2. 时间排序：有时间的在前，按时间顺序
      const aTime = a.startAt || a.dueAt;
      const bTime = b.startAt || b.dueAt;

      if (aTime && bTime) {
        return new Date(aTime).getTime() - new Date(bTime).getTime();
      }
      if (aTime && !bTime) return -1;
      if (!aTime && bTime) return 1;

      // 3. 创建时间排序
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    const sortedCompletedToday = [...completedToday].sort((a, b) => {
      // 1. 完成状态：未完成在前
      if (a.done !== b.done) {
        return a.done ? 1 : -1;
      }

      // 2. 对于已完成的任务，按完成时间倒序（最近完成的在前）
      if (a.done && b.done && a.doneAt && b.doneAt) {
        return new Date(b.doneAt).getTime() - new Date(a.doneAt).getTime();
      }

      // 3. 对于未完成的任务，按创建时间排序
      if (!a.done && !b.done) {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }

      return 0;
    });

    const sortedTemporary = [...temporary].sort((a, b) => {
      // 临时任务按创建时间排序，最新操作的在前
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    // 创建视觉顺序：自然任务 + 临时任务（分割线下方顶部）+ 完成于今天
    const visualList = [...sortedNatural, ...sortedTemporary, ...sortedCompletedToday];
    const orderMap = new Map<string, number>();

    visualList.forEach((task, visualIndex) => {
      orderMap.set(task.id, visualIndex);
    });

    return {
      naturalTasks: sortedNatural,
      completedTodayTasks: sortedCompletedToday,
      temporaryTasks: sortedTemporary,
      visualOrderMap: orderMap
    };
  }, [tasks, temporaryVisibleTasks, currentDate]);

  // 如果没有任务，显示智能空状态
  if (!tasks || tasks.length === 0) {
    return (
      <SmartEmptyState
        currentDate={currentDate}
        allTasks={allTasks}
        onDateChange={onDateChange}
        onNewTask={onNewTask}
      />
    );
  }

  // Recent模式 - 按时间分组显示
  if (specialMode === 'recent') {
    const tasksByTime = groupTasksByTime(tasks);
    const timeGroups: Array<{ key: 'yesterday' | 'today' | 'tomorrow'; tasks: Task[] }> = [
      { key: 'yesterday', tasks: tasksByTime.yesterday },
      { key: 'today', tasks: tasksByTime.today },
      { key: 'tomorrow', tasks: tasksByTime.tomorrow }
    ];

    return (
      <div className={`space-y-4 p-4 ${isDark ? 'bg-ios-dark-gray6' : 'bg-gray-50'}`}>
        {timeGroups.map(({ key, tasks: groupTasks }) => (
          <RecentTimeGroup
            key={key}
            groupKey={key}
            tasks={groupTasks}
            onTaskClick={onTaskClick}
            onTaskToggle={onTaskToggle}
            onTaskUpdate={onTaskUpdate}
            onDateJump={onDateChange}
            onTaskCreated={() => {
              // 任务创建后的回调，可以用于刷新数据
            }}
          />
        ))}
      </div>
    );
  }

  // 时间轴模式 - 只在非特殊模式下可用，只显示真正属于今天的任务
  if (viewMode === 'timeline' && !specialMode) {
    return (
      <TimelineView
        tasks={naturalTasks} // 只传递真正属于今天的任务，排除完成于今天和临时任务
        onTaskToggle={onTaskToggle}
        onTaskClick={onTaskClick}
        onTaskUpdate={onTaskUpdate}
        onNewTask={onNewTask}
        currentDate={currentDate}
        stackMode={stackMode}
      />
    );
  }

  // 列表模式
  return (
    <div className={isDark ? 'bg-ios-dark-gray6' : 'bg-white'}>
      {/* 自然任务列表 */}
      <div>
        {naturalTasks.map((task) => {
          const visualIndex = visualOrderMap.get(task.id) ?? -1;
          const isWelcomeProjectTask = welcomeProjectId && task.projectId === welcomeProjectId;
          return (
            <div
              key={task.id}
              data-task-index={visualIndex}
              data-task-id={task.id}
              data-testid={isWelcomeProjectTask ? "welcome-project" : undefined}
            >
              <TaskItemV2
                task={task}
                onToggle={onTaskToggle}
                onClick={onTaskClick}
                viewMode="list"
                showDatePrefix={specialMode === 'past' || specialMode === 'future'}
                isFocused={visualIndex === focusedTaskIndex}
                isTemporaryVisible={false}
              />
            </div>
          );
        })}
      </div>

      {/* 分割线和完成于今天的任务 - 只在非特殊模式下显示 */}
      {!specialMode && (completedTodayTasks.length > 0 || temporaryTasks.length > 0) && (
        <>
          {/* 分割线 */}
          <div className={`flex items-center my-1 px-1 ${isDark ? 'text-ios-dark-gray3' : 'text-gray-400'}`}>
            <div className={`flex-1 h-px ${isDark ? 'bg-ios-dark-gray4' : 'bg-gray-200'}`}></div>
            <span className="px-3 text-xs font-medium">{t('v3/tasks:completedToday.divider', '完成于今天')}</span>
            <div className={`flex-1 h-px ${isDark ? 'bg-ios-dark-gray4' : 'bg-gray-200'}`}></div>
          </div>

          {/* 临时缓存的任务列表（显示在顶部） */}
          <div>
            {temporaryTasks.map((task) => {
              const visualIndex = visualOrderMap.get(task.id) ?? -1;
              return (
                <div key={task.id} data-task-index={visualIndex} data-task-id={task.id}>
                  <TaskItemV2
                    task={task}
                    onToggle={onTaskToggle}
                    onClick={onTaskClick}
                    viewMode="list"
                    showDatePrefix={true} // 临时任务显示完整日期
                    isFocused={visualIndex === focusedTaskIndex}
                    isTemporaryVisible={true} // 标识为临时显示
                    isCompletedToday={false}
                  />
                </div>
              );
            })}
          </div>

          {/* 完成于今天的任务列表 */}
          <div>
            {completedTodayTasks.map((task) => {
              const visualIndex = visualOrderMap.get(task.id) ?? -1;
              return (
                <div key={task.id} data-task-index={visualIndex} data-task-id={task.id}>
                  <TaskItemV2
                    task={task}
                    onToggle={onTaskToggle}
                    onClick={onTaskClick}
                    viewMode="list"
                    showDatePrefix={false} // 不显示日期前缀
                    isFocused={visualIndex === focusedTaskIndex}
                    isTemporaryVisible={false}
                    isCompletedToday={true} // 标识这是完成于今天的任务
                  />
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
